import { Avatar, CircularProgress, IconButton, makeStyles, Tooltip } from "@material-ui/core";
import MemoryIcon from "@material-ui/icons/Memory";
import React, { useState } from "react";
import { useRaspberryDiagnostics } from "../../../../hooks/useRaspberryDiagnostics";
import { useStore } from "../../../../hooks/useStore";
import { useTranslation } from "../../../../hooks/useTranslation";
import NewEvent from "../../../../passapp-sdk/newEvent";
import { blue900 } from "../constants";
import Toast from "../Toast/Toast";

interface HeaderProps {
  title: string;
  location: string;
  name: string;
}

const useHeaderStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "white",
    display: "flex",
    flexDirection: "row",
    width: "full-content",
    justifyContent: "space-between",
    borderBottom: "1px solid #E5E7EB",
    paddingInline: "32px",
  },
  secondColumn: {
    display: "flex",
    flexDirection: "row",
    gap: "24px",
    justifyContent: "center",
    alignItems: "center",
  },
  ubicationTitle: {
    color: "#6A7282",
    fontSize: "12px",
    textAlign: "right",
    fontWeight: 600,
  },
  ubicationValue: {
    color: "#101828",
    fontSize: "16px",
    fontWeight: 600,
  },
  locationContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
  },
  nameContainer: {
    backgroundColor: "#F9FAFB",
    paddingInline: "16px",
    paddingBlock: "8px",
    height: "fit-content",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  verticalDivider: {
    height: "50%",
    width: "2px",
    backgroundColor: "#E5E7EB",
  },
  name: {
    fontWeight: 600,
    color: "#101828",
    fontSize: "14px",
  },
  /** Misma pildora que nameContainer, para que se lean como un par. */
  extensionButton: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    paddingInline: "16px",
    paddingBlock: "8px",
    color: blue900,
    "&:hover": {
      backgroundColor: "#F3F4F6",
    },
  },
  /** 36px como el Avatar del nombre: iguala la altura de las dos pildoras. */
  extensionIconBox: {
    height: 36,
    width: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  /** Solo cuando un envio real a la Raspberry ya fallo. */
  extensionButtonWarning: {
    color: "#E65100",
    borderColor: "#F9A825",
    backgroundColor: "#FFF8E1",
    "&:hover": {
      backgroundColor: "#FFF3CD",
    },
  },
}));

const Header = ({ title, location, name }: HeaderProps) => {
  const classes = useHeaderStyles();
  const { t, BundleKey } = useTranslation();
  const { authStore } = useStore();
  const raspberryDiagnostics = useRaspberryDiagnostics();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // `title` de Tooltip no acepta el undefined que admite el tipo de retorno de t().
  const extensionLabel = String(t(BundleKey.RB_EXT_MENU));

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await new NewEvent().downloadExtension(authStore.token);
    } catch (error: any) {
      setDownloadError(
        String(t(BundleKey.RB_EXT_DOWNLOAD_ERROR, { error: String(error?.message || error) }))
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={classes.root}>
      <h2>{title}</h2>
      <div className={classes.secondColumn}>
        {/* <div className={classes.locationContainer}>
          <span className={classes.ubicationTitle}>{t(BundleKey.ACTUAL_LOCATION_LABEL)}</span>
          <span className={classes.ubicationValue}>{location}</span>
        </div>
        <div className={classes.verticalDivider} /> */}
        {/* Solo en el header de escritorio: Chrome mobile no soporta extensiones. */}
        <Tooltip title={extensionLabel}>
          <span>
            <IconButton
              aria-label={extensionLabel}
              className={`${classes.extensionButton}${
                raspberryDiagnostics.hasIssue ? ` ${classes.extensionButtonWarning}` : ""
              }`}
              onClick={handleDownload}
              disabled={downloading}
            >
              <span className={classes.extensionIconBox}>
                {downloading ? <CircularProgress size={20} /> : <MemoryIcon />}
              </span>
            </IconButton>
          </span>
        </Tooltip>
        <div className={classes.nameContainer}>
          <Avatar style={{ backgroundColor: blue900, height: 36, width: 36 }} />
          <span className={classes.name}>{name}</span>
        </div>
      </div>
      <Toast
        open={!!downloadError}
        message={downloadError || ""}
        severity="error"
        onClose={() => setDownloadError(null)}
      />
    </div>
  );
};

export default Header;
